import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRight } from 'lucide-react';
import { getRelatedTools, getCategory } from '@/lib/tools';
import { AdSlot } from '@/components/AdSlot';
import { useSeo } from '@/lib/seo';

/**
 * Wraps a calculator page with SEO metadata, breadcrumbs, calculator body,
 * how-it-works content, worked examples, FAQ, and related tools.
 */
export default function ToolPage({
  tool,
  intro,
  howItWorks,
  formula,
  examples = [],
  faqs = [],
  limitations,
  children,
}) {
  const category = getCategory(tool.category);
  const related = getRelatedTools(tool.slug, 4);

  const faqJsonLd = faqs.length ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  } : null;

  useSeo({
    title: tool.seoTitle || tool.name,
    description: tool.description,
    path: `/tools/${tool.slug}`,
    jsonLd: faqJsonLd,
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="text-sm text-slate-500 mb-6">
        <ol className="flex items-center gap-2 flex-wrap">
          <li><Link to="/" className="hover:text-slate-900">Home</Link></li>
          <li><ChevronRight className="w-3.5 h-3.5" /></li>
          <li><Link to="/tools" className="hover:text-slate-900">Tools</Link></li>
          <li><ChevronRight className="w-3.5 h-3.5" /></li>
          <li>
            <Link to={`/categories#${category.id}`} className="hover:text-slate-900">{category.name}</Link>
          </li>
          <li><ChevronRight className="w-3.5 h-3.5" /></li>
          <li className="text-slate-800 font-medium">{tool.name}</li>
        </ol>
      </nav>

      <header className="mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold uppercase tracking-wide mb-4">
          {category.name}
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">{tool.name}</h1>
        <p className="mt-3 text-lg text-slate-600 max-w-2xl">{tool.tagline}</p>
      </header>

      <div className="lg:grid lg:grid-cols-12 lg:gap-10 items-start">
        <div className="lg:col-span-8 space-y-10">
          {/* Calculator */}
          <section
            data-testid={`tool-${tool.slug}-calculator`}
            className="bg-white border border-slate-100 rounded-2xl shadow-[0_2px_10px_rgba(0,0,0,0.03)] p-6 sm:p-8"
          >
            {children}
          </section>

          {/* Intro */}
          {intro && (
            <section>
              <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mb-4">About the {tool.name}</h2>
              <div className="prose prose-slate max-w-none text-slate-600 leading-relaxed space-y-4">
                {intro}
              </div>
            </section>
          )}

          {/* How it works */}
          {howItWorks && (
            <section>
              <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mb-4">How it works</h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">{howItWorks}</div>
            </section>
          )}

          {/* Formula */}
          {formula && (
            <section className="bg-slate-900 rounded-2xl p-6 sm:p-8 text-slate-100">
              <h2 className="text-white text-xl font-semibold mb-3">Formula</h2>
              <div className="font-mono text-emerald-300 text-sm sm:text-base">{formula}</div>
            </section>
          )}

          {/* Ad between long-form content */}
          <AdSlot size="leaderboard" id={`ad-slot-${tool.slug}-mid`} />

          {/* Examples */}
          {examples.length > 0 && (
            <section>
              <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mb-4">Worked examples</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {examples.map((ex, i) => (
                  <div key={i} className="border border-slate-100 rounded-xl p-5 bg-white">
                    <div className="text-xs font-semibold uppercase tracking-wide text-emerald-600 mb-2">Example {i + 1}</div>
                    <div className="text-slate-700 text-sm leading-relaxed">{ex}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* FAQ */}
          {faqs.length > 0 && (
            <section>
              <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mb-4">Frequently asked questions</h2>
              <div className="divide-y divide-slate-100 border border-slate-100 rounded-2xl bg-white">
                {faqs.map((f, i) => (
                  <details key={i} className="group p-5" data-testid={`faq-${tool.slug}-${i}`}>
                    <summary className="cursor-pointer list-none flex justify-between items-center text-slate-900 font-medium">
                      <span>{f.q}</span>
                      <ChevronRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform" />
                    </summary>
                    <div className="mt-3 text-slate-600 leading-relaxed text-sm">{f.a}</div>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* Limitations */}
          {limitations && (
            <section className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
              <h3 className="text-amber-900 font-semibold mb-2">Limitations & disclaimer</h3>
              <div className="text-amber-900/80 text-sm leading-relaxed">{limitations}</div>
            </section>
          )}
        </div>

        <aside className="lg:col-span-4 mt-10 lg:mt-0 space-y-6 lg:sticky lg:top-24">
          <div className="bg-white border border-slate-100 rounded-2xl p-6">
            <h3 className="text-slate-900 font-semibold mb-4">Related tools</h3>
            <ul className="space-y-3">
              {related.map((r) => (
                <li key={r.slug}>
                  <Link
                    to={`/tools/${r.slug}`}
                    data-testid={`related-tool-${r.slug}`}
                    className="group flex items-start justify-between gap-3 py-2 -mx-2 px-2 rounded-lg hover:bg-slate-50"
                  >
                    <div>
                      <div className="text-sm font-medium text-slate-900 group-hover:text-emerald-600">{r.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{r.tagline}</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-600 mt-1 shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <AdSlot size="rectangle" id={`ad-slot-${tool.slug}-sidebar`} />
        </aside>
      </div>
    </div>
  );
}
